import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Button } from "src/@/components/ui/button";
import Iconify from "src/components/Iconify";

export function PopoverVariaveis({ children, onSelectVariavel, variables = [] }) {
  const { t } = useTranslation("automation");
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const isSelectingRef = useRef(false);

  // Fallback if variables are empty
  const displayVariables = variables.length > 0 ? variables : [
    { name: '{{nome}}', desc: t("variables.defaultChatName", "Nome do chat") },
    { name: '{{numero}}', desc: t("variables.defaultChatNumber", "Número do chat") }
  ];

  // Calculate position when opening
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const menuWidth = 300;

      let left = rect.right - menuWidth;
      if (left < 10) left = 10;

      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const preferTop = spaceAbove > spaceBelow && spaceBelow < 280;

      setPosition({
        top: preferTop ? 'auto' : rect.bottom + 4,
        bottom: preferTop ? (window.innerHeight - rect.top + 4) : 'auto',
        left: left
      });
    }
  }, [isOpen]);

  // Close when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (isSelectingRef.current) return;

      const isClickOnTrigger = triggerRef.current?.contains(event.target);
      const isClickOnMenu = menuRef.current?.contains(event.target);

      if (!isClickOnTrigger && !isClickOnMenu) {
        setIsOpen(false);
      }
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 10);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const handleSelect = (variableName) => {
    isSelectingRef.current = true;

    // Clean variable name - remove {{ and }} if present
    const cleanName = variableName.replace(/^\{\{|\}\}$/g, '');

    if (onSelectVariavel && typeof onSelectVariavel === 'function') {
      onSelectVariavel(cleanName);
    }

    setIsOpen(false);

    setTimeout(() => {
      isSelectingRef.current = false;
    }, 0);
  };

  const handleToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(prev => !prev);
  };

  // Menu content rendered via portal
  const menuContent = isOpen ? createPortal(
    <div
      ref={menuRef}
      data-popover-variables-menu="true"
      className="fixed bg-white border border-gray-200 rounded-xl shadow-2xl overflow-hidden"
      style={{
        top: typeof position.top === 'number' ? position.top : 'auto',
        bottom: typeof position.bottom === 'number' ? position.bottom : 'auto',
        left: position.left,
        width: 300,
        zIndex: 999999,
      }}
      onPointerDown={(e) => {
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();
      }}
    >
      <div className="p-3 border-b bg-gray-50/80">
        <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-2">
          <Iconify icon="mdi:code-braces" width={14} />
          {t("variables.popoverTitle", "Variáveis Disponíveis")}
        </h5>
      </div>
      <div className="max-h-[250px] overflow-y-auto">
        {displayVariables.length === 0 ? (
          <div className="py-6 text-center text-sm text-gray-500">
            {t("variables.emptyState", "Nenhuma variável encontrada.")}
          </div>
        ) : (
          displayVariables.map((variable, index) => (
            <div
              key={index}
              className="px-3 py-2 hover:bg-violet-50 cursor-pointer transition-colors border-b border-gray-50 last:border-0"
              role="button"
              tabIndex={0}
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                e.nativeEvent.stopImmediatePropagation();
                // Executar a seleção diretamente no pointerdown
                handleSelect(variable.name);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleSelect(variable.name);
                }
              }}
            >
              <div className="flex items-center gap-2 w-full">
                <Iconify icon="mdi:variable" className="text-violet-500 flex-shrink-0" width={16} />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-violet-700 font-mono truncate">
                    {variable.name}
                  </p>
                  {variable.desc && (
                    <p className="text-[10px] text-gray-500 truncate leading-tight">
                      {variable.desc}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <>
      {/* Trigger */}
      <div
        ref={triggerRef}
        onClick={handleToggle}
        className="inline-block cursor-pointer"
      >
        {children ? children : (
          <Button
            className="w-24 h-8 text-xs"
            variant="secondary"
            type="button"
          >
            <Iconify icon="hugeicons:code" className="mr-2" />
            {t("variables.triggerButton", "Variaveis")}
          </Button>
        )}
      </div>

      {/* Portal menu */}
      {menuContent}
    </>
  );
}
